import { Loader } from "@tql/ui-components-react/kendo/indicators";
import { useMemo } from "react";

import { useGetLoadTracking } from "~/features/loads/hooks/use-get-load-tracking.ts";
import { LocationUpdates } from "~/features/shipment-management/components/tracking-history/components/location-updates.tsx";
import {
	buildStopPings,
	buildTrackingPings,
} from "~/features/track-shipment/utils/shipment-map-utils.ts";
import { TrackingMap } from "~/shared/components/tracking-map/tracking-map.tsx";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import type { Load } from "~/shared/schemas/loads-schema.ts";
import { buildOrderedMapPings } from "~/shared/utils/map-building-utils.ts";
import {
	formatDistanceToNextStop,
	getTrackingStep,
	TRACKING_STEPS,
} from "~/shared/utils/tracking-status.ts";

import styles from "./drawer-tracking.module.css";

type DrawerTrackingProps = {
	readonly load: Load | null;
};

const LTL_RATE_TYPES = new Set(["LTL", "PARTIALS DEPT LTL"]);

const AIR_OCEAN_TYPES = new Set([
	"AIR",
	"CUSTOMS - AIR/OCEAN",
	"DOMESTIC AIR",
	"DOMESTIC AIR GROUND",
	"INTERNATIONAL AIR GROUND",
	"OCEAN",
]);

export function DrawerTracking({ load }: Readonly<DrawerTrackingProps>) {
	const {
		updates: apiUpdates,
		stops: apiStops,
		status: apiStatus,
		hideTracking: apiHideTracking,
		isLoading,
	} = useGetLoadTracking(load?.poNumber);
	const { isInternal } = useDeploymentType();

	const isLtl =
		load?.rateType != null && LTL_RATE_TYPES.has(load.rateType.toUpperCase());
	const isAirOrOcean =
		load?.rateType != null && AIR_OCEAN_TYPES.has(load.rateType.toUpperCase());
	const showDistanceToNextStop = !isLtl && !isAirOrOcean;

	const activeStatus = getTrackingStep(apiStatus);
	const activeStepIndex = TRACKING_STEPS.indexOf(activeStatus);

	const sortedUpdates = useMemo(() => {
		return [...apiUpdates].sort(
			(a, b) => Date.parse(b.sortDate || "") - Date.parse(a.sortDate || ""),
		);
	}, [apiUpdates]);

	const distanceToNextStop = useMemo(() => {
		if (!showDistanceToNextStop) {
			return null;
		}
		const formatted = formatDistanceToNextStop(sortedUpdates[0]?.distance);
		return formatted;
	}, [showDistanceToNextStop, sortedUpdates]);

	const trackingPings = useMemo(
		() => buildTrackingPings(sortedUpdates),
		[sortedUpdates],
	);

	const stopPings = useMemo(() => buildStopPings(apiStops), [apiStops]);

	const mapPings = useMemo(
		() => buildOrderedMapPings(trackingPings, stopPings),
		[trackingPings, stopPings],
	);

	return (
		<div className={styles.wrapper}>
			{isLoading ? (
				<div className={styles.loaderContainer}>
					<Loader type={"infinite-spinner"} />
				</div>
			) : (
				<>
					<div aria-label={"Tracking status"} className={styles.statusTabs}>
						{TRACKING_STEPS.map((step, index) => {
							const isActive = step === activeStatus;
							const isCompleted = index < activeStepIndex;
							const tabClass = [
								styles.statusTab,
								isActive ? styles.activeStatusTab : "",
								isCompleted ? styles.completedStatusTab : "",
							]
								.filter(Boolean)
								.join(" ");

							return (
								<span key={step} className={tabClass}>
									{step}
								</span>
							);
						})}
					</div>
					{isAirOrOcean ? (
						<div className={styles.externalTrackingContainer}>
							<a
								className={styles.externalTrackingLink}
								href={
									"https://trax.tqlglobal.com/Portals/NEO/Desktop#/login/index"
								}
								rel={"noopener noreferrer"}
								target={"_blank"}
							>
								Track via TQL Global Portal
							</a>
						</div>
					) : (
						<>
							<LocationUpdates
								distanceToNextStop={distanceToNextStop}
								hideTrackingData={apiHideTracking}
								updates={sortedUpdates}
							/>
							{mapPings.length === 0 || (isLtl && isInternal) ? null : (
								<TrackingMap pings={mapPings} />
							)}
						</>
					)}
				</>
			)}
		</div>
	);
}
